/**
 * Client-side board template definitions.
 *
 * Each definition carries stable `templateId` and `statusId` slugs used as
 * i18n keys by the presentation layer. Display strings (title, description,
 * status names) are resolved through the active locale via
 * `useResolvedBoardTemplates()` — never hardcoded here.
 *
 * The `applyToBoard` API call receives the resolved template, so the server
 * applies whatever column structure and names the client sends. The server
 * no longer needs to own template definitions.
 */

export interface TemplateStatusDefinition {
  readonly statusId: string;
  /** i18n key: `board.templates.{templateId}.statuses.{statusId}.name` */
  readonly nameKey: string;
  readonly isDone: boolean;
  readonly scheduleDateDays?: number;
  /** Hex colour hint — optional, falls back to the wizard palette */
  readonly colour?: string;
}

export interface BoardTemplateDefinition {
  readonly templateId: string;
  /** i18n key: `board.templates.{templateId}.title` */
  readonly titleKey: string;
  /** i18n key: `board.templates.{templateId}.description` */
  readonly descriptionKey: string;
  readonly iconUrl: string;
  readonly statuses: readonly TemplateStatusDefinition[];
}

export const BOARD_TEMPLATE_DEFINITIONS: readonly BoardTemplateDefinition[] = [
  // ── To-Do > Done ────────────────────────────────────────────────────────────
  {
    templateId: "toDoDoingDone",
    titleKey: "board.templates.toDoDoingDone.title",
    descriptionKey: "board.templates.toDoDoingDone.description",
    iconUrl: "fas fa-columns",
    statuses: [
      { statusId: "incomingTasks", nameKey: "board.templates.toDoDoingDone.statuses.incomingTasks.name", isDone: false },
      { statusId: "toDo",          nameKey: "board.templates.toDoDoingDone.statuses.toDo.name",          isDone: false },
      { statusId: "doing",         nameKey: "board.templates.toDoDoingDone.statuses.doing.name",         isDone: false },
      { statusId: "done",          nameKey: "board.templates.toDoDoingDone.statuses.done.name",          isDone: true  },
    ],
  },

  // ── To-Do > Done (advanced) ──────────────────────────────────────────────────
  {
    templateId: "toDoDoingDone2",
    titleKey: "board.templates.toDoDoingDone2.title",
    descriptionKey: "board.templates.toDoDoingDone2.description",
    iconUrl: "fas fa-columns",
    statuses: [
      { statusId: "incomingTasks", nameKey: "board.templates.toDoDoingDone2.statuses.incomingTasks.name", isDone: false },
      { statusId: "toDo",          nameKey: "board.templates.toDoDoingDone2.statuses.toDo.name",          isDone: false },
      { statusId: "almostThere",   nameKey: "board.templates.toDoDoingDone2.statuses.almostThere.name",   isDone: false },
      { statusId: "doing",         nameKey: "board.templates.toDoDoingDone2.statuses.doing.name",         isDone: false },
      { statusId: "onHold",        nameKey: "board.templates.toDoDoingDone2.statuses.onHold.name",        isDone: false },
      { statusId: "done",          nameKey: "board.templates.toDoDoingDone2.statuses.done.name",          isDone: true  },
    ],
  },

  // ── Single List ──────────────────────────────────────────────────────────────
  {
    templateId: "singleList",
    titleKey: "board.templates.singleList.title",
    descriptionKey: "board.templates.singleList.description",
    iconUrl: "fas fa-tasks",
    statuses: [
      { statusId: "taskList", nameKey: "board.templates.singleList.statuses.taskList.name", isDone: false },
    ],
  },

  // ── Day / Week / Month ───────────────────────────────────────────────────────
  {
    templateId: "dayWeekMonth",
    titleKey: "board.templates.dayWeekMonth.title",
    descriptionKey: "board.templates.dayWeekMonth.description",
    iconUrl: "fas fa-calendar-check",
    statuses: [
      { statusId: "incomingTasks", nameKey: "board.templates.dayWeekMonth.statuses.incomingTasks.name", isDone: false },
      { statusId: "someday",       nameKey: "board.templates.dayWeekMonth.statuses.someday.name",       isDone: false },
      { statusId: "thisMonth",     nameKey: "board.templates.dayWeekMonth.statuses.thisMonth.name",     isDone: false, scheduleDateDays: 31 },
      { statusId: "thisWeek",      nameKey: "board.templates.dayWeekMonth.statuses.thisWeek.name",      isDone: false, scheduleDateDays: 7  },
      { statusId: "tomorrow",      nameKey: "board.templates.dayWeekMonth.statuses.tomorrow.name",      isDone: false, scheduleDateDays: 1  },
      { statusId: "today",         nameKey: "board.templates.dayWeekMonth.statuses.today.name",         isDone: false, scheduleDateDays: 0  },
      { statusId: "done",          nameKey: "board.templates.dayWeekMonth.statuses.done.name",          isDone: true  },
    ],
  },

  // ── Eisenhower Matrix ────────────────────────────────────────────────────────
  {
    templateId: "eisenhowerMatrix",
    titleKey: "board.templates.eisenhowerMatrix.title",
    descriptionKey: "board.templates.eisenhowerMatrix.description",
    iconUrl: "fas fa-th-large",
    statuses: [
      { statusId: "incomingTasks", nameKey: "board.templates.eisenhowerMatrix.statuses.incomingTasks.name", isDone: false },
      { statusId: "delete",        nameKey: "board.templates.eisenhowerMatrix.statuses.delete.name",        isDone: false },
      { statusId: "delegate",      nameKey: "board.templates.eisenhowerMatrix.statuses.delegate.name",      isDone: false },
      { statusId: "decide",        nameKey: "board.templates.eisenhowerMatrix.statuses.decide.name",        isDone: false },
      { statusId: "doItNow",       nameKey: "board.templates.eisenhowerMatrix.statuses.doItNow.name",       isDone: true  },
    ],
  },

  // ── Agile Software Development ───────────────────────────────────────────────
  {
    templateId: "software",
    titleKey: "board.templates.software.title",
    descriptionKey: "board.templates.software.description",
    iconUrl: "fas fa-laptop",
    statuses: [
      { statusId: "backlog",    nameKey: "board.templates.software.statuses.backlog.name",    isDone: false },
      { statusId: "approved",   nameKey: "board.templates.software.statuses.approved.name",   isDone: false },
      { statusId: "committed",  nameKey: "board.templates.software.statuses.committed.name",  isDone: false },
      { statusId: "testing",    nameKey: "board.templates.software.statuses.testing.name",    isDone: false },
      { statusId: "released",   nameKey: "board.templates.software.statuses.released.name",   isDone: true  },
    ],
  },

  // ── Getting Things Done (GTD) ────────────────────────────────────────────────
  {
    templateId: "gtd",
    titleKey: "board.templates.gtd.title",
    descriptionKey: "board.templates.gtd.description",
    iconUrl: "fas fa-inbox",
    statuses: [
      { statusId: "inbox",       nameKey: "board.templates.gtd.statuses.inbox.name",       isDone: false },
      { statusId: "nextAction",  nameKey: "board.templates.gtd.statuses.nextAction.name",  isDone: false },
      { statusId: "waitingFor",  nameKey: "board.templates.gtd.statuses.waitingFor.name",  isDone: false },
      { statusId: "someday",     nameKey: "board.templates.gtd.statuses.someday.name",     isDone: false },
      { statusId: "done",        nameKey: "board.templates.gtd.statuses.done.name",        isDone: true  },
    ],
  },

  // ── Personal Kanban ──────────────────────────────────────────────────────────
  {
    templateId: "personalKanban",
    titleKey: "board.templates.personalKanban.title",
    descriptionKey: "board.templates.personalKanban.description",
    iconUrl: "fas fa-stream",
    statuses: [
      { statusId: "backlog", nameKey: "board.templates.personalKanban.statuses.backlog.name", isDone: false },
      { statusId: "doing",   nameKey: "board.templates.personalKanban.statuses.doing.name",   isDone: false },
      { statusId: "done",    nameKey: "board.templates.personalKanban.statuses.done.name",    isDone: true  },
    ],
  },

  // ── Content & Editorial ──────────────────────────────────────────────────────
  {
    templateId: "contentEditorial",
    titleKey: "board.templates.contentEditorial.title",
    descriptionKey: "board.templates.contentEditorial.description",
    iconUrl: "fas fa-pen-nib",
    statuses: [
      { statusId: "ideas",     nameKey: "board.templates.contentEditorial.statuses.ideas.name",     isDone: false },
      { statusId: "writing",   nameKey: "board.templates.contentEditorial.statuses.writing.name",   isDone: false },
      { statusId: "review",    nameKey: "board.templates.contentEditorial.statuses.review.name",    isDone: false },
      { statusId: "scheduled", nameKey: "board.templates.contentEditorial.statuses.scheduled.name", isDone: false },
      { statusId: "published", nameKey: "board.templates.contentEditorial.statuses.published.name", isDone: true  },
    ],
  },

  // ── Customer Support ─────────────────────────────────────────────────────────
  {
    templateId: "customerSupport",
    titleKey: "board.templates.customerSupport.title",
    descriptionKey: "board.templates.customerSupport.description",
    iconUrl: "fas fa-headset",
    statuses: [
      { statusId: "open",              nameKey: "board.templates.customerSupport.statuses.open.name",              isDone: false },
      { statusId: "inProgress",        nameKey: "board.templates.customerSupport.statuses.inProgress.name",        isDone: false },
      { statusId: "waitingOnCustomer", nameKey: "board.templates.customerSupport.statuses.waitingOnCustomer.name", isDone: false },
      { statusId: "resolved",          nameKey: "board.templates.customerSupport.statuses.resolved.name",          isDone: true  },
    ],
  },

  // ── Hiring Pipeline ──────────────────────────────────────────────────────────
  {
    templateId: "hiringPipeline",
    titleKey: "board.templates.hiringPipeline.title",
    descriptionKey: "board.templates.hiringPipeline.description",
    iconUrl: "fas fa-user-plus",
    statuses: [
      { statusId: "applied",      nameKey: "board.templates.hiringPipeline.statuses.applied.name",      isDone: false },
      { statusId: "phoneScreen",  nameKey: "board.templates.hiringPipeline.statuses.phoneScreen.name",  isDone: false },
      { statusId: "interview",    nameKey: "board.templates.hiringPipeline.statuses.interview.name",    isDone: false },
      { statusId: "offer",        nameKey: "board.templates.hiringPipeline.statuses.offer.name",        isDone: false },
      { statusId: "hired",        nameKey: "board.templates.hiringPipeline.statuses.hired.name",        isDone: true  },
    ],
  },
];
